import React, { useRef, useEffect, useState, useCallback } from 'react';
import LocationSection from './Form1/LocationSection';
import DetailsSection from './Form1/DetailsSection';
import TaxVydelSection from './Form1/TaxVydelSection';
import ForestPathVydelSection from './Form1/ForestPathVydelSection';
import InfoSection from './Form1/InfoSection';
import MapModal from './Form1/MapModal';
import PickerModal from './Form1/PickerModal';
import { getEmptyFormData } from './Form1/constants';
import { useMapModal } from './Form1/hooks';
import { useDataLoading } from './Form1/dataHooks';
import { handbookById, resolveSubjectRefId, HANDBOOK_SUBJECTS_ID } from './Form1/pickerHooks';
import { useCardPicker } from './Form1/cardPickerHooks';
import { useFormValidation, useClearTableData } from './Form1/formHooks';

const Form1 = ({ handbooks, formData, setFormData, selectedDoc, user, isNewCard = false }) => {
  // Используем formData из props напрямую
  const localFormData = formData || getEmptyFormData();
  const setLocalFormData = setFormData;

  // Используем localFormData для отображения, чтобы показывать актуальные данные
  const displayData = localFormData || getEmptyFormData();

  // Используем dependentData из localFormData (displayData)
  const dependentData = displayData.dependentData || {
    organizations: [],
    forestDistricts: [],
    subDistricts: [],
    toponyms: [],
    subjectDataLoadedFor: '',
  };
  
  const setDependentDataRef = useRef();
  setDependentDataRef.current = (newData) => {
    setLocalFormData(prev => ({
      ...prev,
      dependentData: typeof newData === 'function' ? newData(prev.dependentData || {}) : newData
    }));
  };
  
  // Используем хуки для валидации
  useFormValidation(localFormData, setLocalFormData, handbooks);

  // Используем хуки для загрузки данных
  const { loadSubjectData } = useDataLoading(
    setDependentDataRef,
    handbooks
  );

  // Используем хук для работы с картой
  const {
    showMapModal,
    mapPosition,
    mapRef,
    openMap,
    closeMap,
    getCurrentLocation,
    selectCoordinates,
    updateMapPosition
  } = useMapModal(localFormData, setLocalFormData, setFormData);

  // Используем хук для работы с picker
  const {
    showPicker,
    pickerValue,
    pickerSearch,
    openPicker,
    closePicker,
    selectPickerValue,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch
  } = useCardPicker(handbooks, localFormData, dependentData, setFormData, setLocalFormData, user, isNewCard);

  // Используем хук для очистки данных
  const clearTableData = useClearTableData(localFormData, setLocalFormData, setFormData);

  // Состояние для скроллбара таблицы информации
  const [infoTableScrollPosition, setInfoTableScrollPosition] = useState(0);
  const infoTableScrollRef = useRef(null);

  // Функции для работы со скроллбаром таблицы информации
  const handleInfoTableScroll = useCallback(() => {
    if (infoTableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = infoTableScrollRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const percentage = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setInfoTableScrollPosition(percentage);
    }
  }, []);

  const handleInfoTableSliderChange = useCallback((e) => {
    const newPosition = parseInt(e.target.value);
    setInfoTableScrollPosition(newPosition);
    if (infoTableScrollRef.current) {
      const maxScroll = infoTableScrollRef.current.scrollWidth - infoTableScrollRef.current.clientWidth;
      const scrollLeft = (newPosition / 100) * maxScroll;
      infoTableScrollRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Отслеживаем предыдущие значения для определения изменений
  const prevSrfRef = useRef(localFormData.srf);
  const prevFilialRef = useRef(localFormData.filial);
  /** Уже запрошенные данные субъекта для этого значения srf (избегает цикла при пустом branches). */
  const subjectDataRequestedForSrfRef = useRef(null);
  // Эффект для загрузки зависимых данных при изменении субъекта РФ и сброса зависимых полей
  useEffect(() => {
    const currentSrf = localFormData.srf;
    const prevSrf = prevSrfRef.current;

    // Если субъект изменился, сбрасываем все зависимые поля
    if (prevSrf !== undefined && prevSrf !== '' && currentSrf !== prevSrf) {
      subjectDataRequestedForSrfRef.current = null;
      setLocalFormData(prev => ({
        ...prev,
        filial: '',
        lvo: '',
        ulvo: '',
        urc: '',
        lvoId: '', ulvoId: '', urcId: '', isUrcRequired: false,
        dependentData: {
          organizations: [],
          forestDistricts: [],
          subDistricts: [],
          toponyms: [],
          subjectDataLoadedFor: '',
        }
      }));
    }

    prevSrfRef.current = currentSrf;

    if (!currentSrf) {
      subjectDataRequestedForSrfRef.current = null;
      return;
    }

    const handbook = handbookById(handbooks, HANDBOOK_SUBJECTS_ID);
    if (!handbook?.references) return;

    const srfKey = resolveSubjectRefId(currentSrf, handbooks);
    if (!srfKey) return;

    const selectedOption = handbook.references.find((r) => String(r.id) === srfKey);
    if (!selectedOption) return;

    if (subjectDataRequestedForSrfRef.current === srfKey) {
      return;
    }
    subjectDataRequestedForSrfRef.current = srfKey;
    void loadSubjectData(selectedOption.name, selectedOption.id).then((ok) => {
      if (!ok) subjectDataRequestedForSrfRef.current = null;
    });
  }, [localFormData.srf, handbooks, loadSubjectData]);

  // Эффект для сброса зависимых полей при изменении организации
  useEffect(() => {
    const currentFilial = localFormData.filial;
    const prevFilial = prevFilialRef.current;
    
    // Если организация изменилась, сбрасываем все зависимые поля
    if (prevFilial !== undefined && prevFilial !== '' && currentFilial !== prevFilial) {
      setLocalFormData(prev => ({
        ...prev,
        lvo: '',
        ulvo: '',
        urc: '',
        lvoId: '', ulvoId: '', urcId: '', isUrcRequired: false,
        dependentData: {
          ...prev.dependentData,
          forestDistricts: [],
          subDistricts: [],
          toponyms: []
        }
      }));
    }
    
    // Обновляем ref для следующего рендера
    prevFilialRef.current = currentFilial;
  }, [localFormData.filial, setLocalFormData]);

  return (
    <>
      <div className="template-section">
      <div 
        className="template-header" 
        style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h3>I. Форма ввода данных «Лесохозяйственного» адреса участка</h3>
      </div>
      
      <div className="template-content">
        <LocationSection 
          displayData={displayData}
          localFormData={localFormData}
          setLocalFormData={setLocalFormData}
          openMap={openMap}
          clearTableData={clearTableData}
        />
        <DetailsSection 
          handbooks={handbooks}
          displayData={displayData}
          localFormData={localFormData}
          setLocalFormData={setLocalFormData}
          openPicker={openPicker}
          clearTableData={clearTableData}
        />
        <TaxVydelSection 
          displayData={displayData}
          localFormData={localFormData}
          setLocalFormData={setLocalFormData}
        />
        <ForestPathVydelSection 
          displayData={displayData}
          localFormData={localFormData}
          setLocalFormData={setLocalFormData}
        />
        <InfoSection 
          handbooks={handbooks}
          displayData={displayData}
          localFormData={localFormData}
          setLocalFormData={setLocalFormData}
          openPicker={openPicker}
          clearTableData={clearTableData}
          infoTableScrollRef={infoTableScrollRef}
          infoTableScrollPosition={infoTableScrollPosition}
          handleInfoTableScroll={handleInfoTableScroll}
          handleInfoTableSliderChange={handleInfoTableSliderChange}
        />
      </div>
      
      <MapModal 
        showMapModal={showMapModal}
        mapRef={mapRef}
        mapPosition={mapPosition}
        closeMap={closeMap}
        getCurrentLocation={getCurrentLocation}
        selectCoordinates={selectCoordinates}
        updateMapPosition={updateMapPosition}
      />
      <PickerModal 
        showPicker={showPicker}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        getFilteredOptions={getFilteredOptions}
        getSelectedIndexInFiltered={getSelectedIndexInFiltered}
        pickerValue={pickerValue}
        selectPickerValue={selectPickerValue}
        closePicker={closePicker}
      />
    </div>
    </>
  );
};

export default Form1;
